/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop -- Inline style values are computed dynamically per-edge based on selection state; will migrate to css prop when refactoring. */
/**
 * ObjectEdge - Straight edge with selection opacity and shortest-path animation.
 *
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { memo } from 'react';

import { BaseEdge, getStraightPath, type EdgeProps, type Edge } from '@xyflow/react';

import { jsx } from '@atlaskit/css';

import { useGraphSelection, computeEdgeOpacity } from '../context/GraphSelectionContext';

export type ObjectEdgeData = {
	relationshipType: string;
	displayLabel?: string;
	properties?: Record<string, any>;
	color?: string;
};

export type ObjectEdgeType = Edge<ObjectEdgeData, 'objectEdge'>;

const EDGE_COLOR = '#505258';
const PATH_COLOR = '#6ea8fe';

export const ObjectEdge: React.MemoExoticComponent<
	({
		id,
		source,
		target,
		sourceX,
		sourceY,
		targetX,
		targetY,
		markerEnd,
	}: EdgeProps<ObjectEdgeType>) => JSX.Element
> = memo(
	({
		id,
		source,
		target,
		sourceX,
		sourceY,
		targetX,
		targetY,
		markerEnd,
	}: EdgeProps<ObjectEdgeType>): JSX.Element => {
		const [edgePath] = getStraightPath({
			sourceX,
			sourceY,
			targetX,
			targetY,
		});

		const graphSelection = useGraphSelection();
		const edgeOpacity = computeEdgeOpacity(
			String(source ?? ''),
			String(target ?? ''),
			graphSelection,
		);

		const pairKey = `${source}::${target}`;
		const reversePairKey = `${target}::${source}`;
		const isAnimated =
			graphSelection?.animatedEdgePairs.has(pairKey) ||
			graphSelection?.animatedEdgePairs.has(reversePairKey);

		return (
			<React.Fragment>
				<BaseEdge
					id={id}
					path={edgePath}
					markerEnd={markerEnd}
					interactionWidth={0}
					style={{
						stroke: isAnimated ? PATH_COLOR : EDGE_COLOR,
						strokeWidth: isAnimated ? 1.5 : 1,
						strokeDasharray: isAnimated ? '6,4' : undefined,
						opacity: isAnimated ? 0.85 : edgeOpacity,
						transition: 'opacity 0.3s ease-out, stroke 0.2s ease-out',
						animation: isAnimated ? 'edgeFlow 0.8s linear infinite' : undefined,
						pointerEvents: 'none',
					}}
				/>
			</React.Fragment>
		);
	},
);

ObjectEdge.displayName = 'ObjectEdge';
